import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, ScanLine, ArrowLeftRight, FolderLock, Store, BellRing,
  ArrowRight, Check, QrCode,
} from "lucide-react";
import { Logo } from "../components/Logo";
import { ThemeToggle } from "../components/ThemeToggle";
import { useAuth } from "../context/AuthContext";

const fade = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] } }),
};

const VIEWPORT_ONCE = { once: true };

const FEATURES = [
  { icon: ScanLine, title: "Instant digital receipts", body: "Retailers issue a receipt in seconds. Customers claim it with a single QR scan — no paper, no email hunting." },
  { icon: ShieldCheck, title: "Warranty tracking", body: "Every receipt tracks its warranty window and reminds you before cover runs out." },
  { icon: ArrowLeftRight, title: "Ownership transfers", body: "Sold your gadget? Transfer the proof of purchase and warranty to the new owner instantly." },
  { icon: FolderLock, title: "Proof Packs", body: "Bundle receipts into a shareable pack for insurance claims, resale, or tax time." },
  { icon: Store, title: "Retailer console", body: "Onboard stores, manage staff and issue branded receipts from any browser." },
  { icon: BellRing, title: "Never lose a receipt", body: "Your entire purchase history, warranties and proofs live safely in one wallet." },
];

function Nav() {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-8 md:flex">
          <a href="#features" className="text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground">Features</a>
          <a href="#how" className="text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground">How it works</a>
          <a href="#retailers" className="text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground">Retailers</a>
        </nav>
        <div className="flex items-center gap-2.5">
          <ThemeToggle />
          {user ? (
            <Link to="/app" data-testid="nav-app-btn" className="rounded-full bg-brand px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-brand-hover">
              Open app
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-btn" className="hidden rounded-full px-4 py-2 text-sm font-bold text-foreground transition-colors hover:bg-accent sm:block">
                Sign in
              </Link>
              <Link to="/register" data-testid="nav-register-btn" className="rounded-full bg-brand px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-brand-hover">
                Get started
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <Nav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 grain opacity-[0.04]" />
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-brand/20 blur-3xl" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 lg:grid-cols-12 lg:px-8 lg:py-24">
          <div className="lg:col-span-7">
            <motion.div variants={fade} initial="hidden" animate="show" custom={0}
              className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5 text-xs font-bold uppercase tracking-widest text-muted-foreground">
              <QrCode className="h-3.5 w-3.5 text-brand" /> Digital proof of purchase
            </motion.div>
            <motion.h1 variants={fade} initial="hidden" animate="show" custom={1}
              className="font-heading text-5xl font-black leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
              Every receipt.<br /> Every warranty.<br />
              <span className="text-brand">One tap away.</span>
            </motion.h1>
            <motion.p variants={fade} initial="hidden" animate="show" custom={2}
              className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
              ProofPass turns paper receipts into secure digital proof. Track warranties, transfer ownership,
              and bundle proofs — for shoppers and retailers across Australia.
            </motion.p>
            <motion.div variants={fade} initial="hidden" animate="show" custom={3} className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-get-started"
                className="group inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-base font-bold text-white transition-colors hover:bg-brand-hover">
                Start free <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a href="#retailers"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-7 py-3.5 text-base font-bold transition-colors hover:bg-accent">
                For retailers
              </a>
            </motion.div>
            <motion.div variants={fade} initial="hidden" animate="show" custom={4} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              {["No paper receipts", "Warranty reminders", "Bank-grade security"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-success" />{t}</span>
              ))}
            </motion.div>
          </div>

          <motion.div variants={fade} initial="hidden" animate="show" custom={3} className="lg:col-span-5">
            <div className="relative mx-auto max-w-sm">
              <div className="absolute -inset-4 rounded-[2rem] bg-brand/10 blur-2xl" />
              <div className="relative overflow-hidden rounded-[2rem] border border-border bg-card shadow-2xl">
                <img src="https://images.unsplash.com/photo-1600147131759-880e94a6185f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Customer scanning a QR receipt" className="h-56 w-full object-cover" />
                <div className="space-y-4 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Volt Electronics</p>
                      <p className="font-heading text-xl font-extrabold">Sony WH-1000XM5</p>
                    </div>
                    <span className="rounded-full bg-success/12 px-2.5 py-0.5 text-xs font-bold text-success">Active</span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-secondary p-4">
                    <span className="text-sm text-muted-foreground">Warranty</span>
                    <span className="font-mono text-sm font-bold">11 months left</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-black font-heading">$549.00</span>
                    <span className="grid h-12 w-12 place-items-center rounded-xl border border-border">
                      <QrCode className="h-7 w-7" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-border bg-secondary/40">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.22em] text-brand">Everything in one wallet</p>
            <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Built for how people actually buy</h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} variants={fade} initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} custom={i % 3}
                className="group rounded-2xl border border-border bg-card p-7 transition-all hover:-translate-y-1 hover:shadow-lg">
                <span className="mb-5 grid h-12 w-12 place-items-center rounded-xl bg-brand-muted text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                  <f.icon className="h-6 w-6" />
                </span>
                <h3 className="font-heading text-lg font-bold">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="mb-12 max-w-2xl">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.22em] text-brand">How it works</p>
          <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Three steps to paperless proof</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { n: "01", t: "Retailer issues", d: "Staff create a digital receipt with a QR code — quick mode or photo autofill." },
            { n: "02", t: "Customer claims", d: "Scan the QR to save the receipt straight into your ProofPass wallet." },
            { n: "03", t: "Track & share", d: "Monitor warranties, transfer ownership, and export shareable Proof Packs." },
          ].map((s, i) => (
            <motion.div key={s.n} variants={fade} initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} custom={i}
              className="relative rounded-2xl border border-border bg-card p-8">
              <span className="font-heading text-5xl font-black text-brand/20">{s.n}</span>
              <h3 className="mt-3 font-heading text-xl font-bold">{s.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Retailers CTA */}
      <section id="retailers" className="border-t border-border bg-[#09090B] text-white">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-20 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.22em] text-brand">For retailers</p>
            <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Turn every sale into a lasting connection</h2>
            <p className="mt-4 max-w-lg text-white/70">Onboard your stores, issue branded digital receipts, and give customers warranty peace of mind — all from the browser.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="retailer-cta"
                className="rounded-full bg-brand px-7 py-3.5 text-base font-bold text-white transition-colors hover:bg-brand-hover">
                Create retailer account
              </Link>
            </div>
          </div>
          <div className="overflow-hidden rounded-2xl border border-white/10">
            <img src="https://images.unsplash.com/photo-1765009433753-c7462637d21f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Modern retail store counter" className="h-72 w-full object-cover" />
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-5 py-8 text-sm text-muted-foreground sm:flex-row lg:px-8">
          <Logo />
          <p>© {new Date().getFullYear()} ProofPass. Made in Australia.</p>
        </div>
      </footer>
    </div>
  );
}
